// src/lib/contractFieldRules.js — the Desk's copy of what a template field spec says about its own value.
// The server's twin is server-new-libs/lib/contract_field_rules.js; the two must agree, because one refuses
// on save what the other marks before Save (2026-09-24, the owner mandate):
//   "min" / "max"            inclusive bounds on an int or money field
//   "decimals"               how many decimal places an int keeps (absent = a whole number)
//   "after": "<date key>"    a date that must fall strictly after another date
//   kind "rows"              a small table: `columns: [{key, kind: text|textarea|date|enum, required, options?}]`
// ⚠️ Empty is never a range problem — the required-field gate speaks for an empty box.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const hasDecimals = f => Number.isInteger(f?.decimals) && f.decimals > 0

/** The step an <input type=number> should carry for this spec. */
export const numberStep = f => (hasDecimals(f) ? String(10 ** -Math.min(f.decimals, 6)) : '1')

/** A typed number as the form stores it: kept with its decimals when the spec has them, else whole. */
export function numberFromInput(f, raw) {
  if (raw === '' || raw == null) return ''
  const n = Number(String(raw).replace(',', '.'))
  if (!Number.isFinite(n)) return raw
  if (hasDecimals(f)) { const k = 10 ** Math.min(f.decimals, 6); return Math.round(n * k) / k }
  return Math.trunc(n)
}

/** Why this value breaks its spec's own limits → {min,max} | {after} | null. `values` = all the form's values. */
export function rangeProblem(f, v, values = {}) {
  if (f?.kind === 'date' && f.after) {
    const a = String(v || '').slice(0, 10), b = String(values?.[f.after] || '').slice(0, 10)
    return ISO_DATE.test(a) && ISO_DATE.test(b) && a <= b ? { after: f.after } : null
  }
  if (f?.kind !== 'int' && f?.kind !== 'money') return null
  if (f.min == null && f.max == null) return null
  const raw = f.kind === 'money' ? (v && typeof v === 'object' ? v.value : null) : v
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  if ((f.min != null && n < Number(f.min)) || (f.max != null && n > Number(f.max))) return { min: f.min ?? null, max: f.max ?? null }
  return null
}

/** A rows value with every blank row dropped — what the server stores. */
export function filledRows(f, v) {
  const cols = (f?.columns || []).map(c => c.key)
  return (Array.isArray(v) ? v : []).filter(r => r && typeof r === 'object' && cols.some(k => String(r[k] ?? '').trim() !== ''))
}

/** Rows holding a required cell left blank → [{row, column}] (row = index among the FILLED rows). */
export function rowsProblems(f, v) {
  const out = []
  filledRows(f, v).forEach((r, i) => {
    for (const c of f.columns || []) if (c.required && String(r[c.key] ?? '').trim() === '') out.push({ row: i, column: c.key })
  })
  return out
}
